// The Canvas2D surface the art generators draw on. It is a structural subset of the DOM
// CanvasRenderingContext2D that @napi-rs/canvas (Node, `npm run bake` and tests/art) also satisfies, so
// every generator runs unchanged in the browser and in Node. No `filter`, no `fillText` (§2.9, §0.5).

export interface Ctx2D {
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineJoin: CanvasLineJoin;
  lineCap: CanvasLineCap;
  globalAlpha: number;
  globalCompositeOperation: GlobalCompositeOperation;
  shadowBlur: number;
  shadowColor: string;
  shadowOffsetX: number;
  shadowOffsetY: number;
  imageSmoothingEnabled: boolean;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(a: number): void;
  scale(x: number, y: number): void;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
  bezierCurveTo(c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number): void;
  arc(x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean): void;
  arcTo(x1: number, y1: number, x2: number, y2: number, r: number): void;
  ellipse(
    x: number,
    y: number,
    rx: number,
    ry: number,
    rot: number,
    a0: number,
    a1: number,
    ccw?: boolean,
  ): void;
  rect(x: number, y: number, w: number, h: number): void;
  fill(rule?: CanvasFillRule): void;
  stroke(): void;
  clip(rule?: CanvasFillRule): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  setLineDash(segments: number[]): void;
  createRadialGradient(
    x0: number,
    y0: number,
    r0: number,
    x1: number,
    y1: number,
    r1: number,
  ): CanvasGradientLike;
  drawImage(
    image: unknown,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ): void;
  getImageData(x: number, y: number, w: number, h: number): { data: Uint8ClampedArray };
  createImageData(w: number, h: number): ImageDataLike;
  putImageData(img: ImageDataLike, x: number, y: number): void;
}

export interface ImageDataLike {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
}

export interface CanvasGradientLike {
  addColorStop(offset: number, color: string): void;
}

/** An HTMLCanvasElement, an OffscreenCanvas or an @napi-rs/canvas Canvas. */
export interface CanvasLike {
  width: number;
  height: number;
}

export interface CanvasFactory {
  create(w: number, h: number): CanvasLike;
  context(c: CanvasLike): Ctx2D;
}

/** Browser default: a DOM canvas (Phaser's addCanvas wants an HTMLCanvasElement), else OffscreenCanvas. */
function browserFactory(): CanvasFactory {
  return {
    create(w, h) {
      if (typeof document !== 'undefined') {
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        return c;
      }
      if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
      throw new Error('art: no canvas available (call setCanvasFactory in Node)');
    },
    context(c) {
      const ctx = (c as HTMLCanvasElement).getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error('art: 2d context unavailable');
      return ctx as unknown as Ctx2D;
    },
  };
}

let factory: CanvasFactory | null = null;

/** Node (`npm run bake`, tests/art) installs an @napi-rs/canvas factory; the browser uses the default. */
export function setCanvasFactory(f: CanvasFactory): void {
  factory = f;
}

export function canvasFactory(): CanvasFactory {
  factory ??= browserFactory();
  return factory;
}

export function createCanvas(w: number, h: number): CanvasLike {
  return canvasFactory().create(Math.max(1, w), Math.max(1, h));
}

export function contextOf(c: CanvasLike): Ctx2D {
  return canvasFactory().context(c);
}

/** Frees a scratch canvas's backing store (§2.9: "scratch canvases are freed after upload"). */
export function freeCanvas(c: CanvasLike): void {
  c.width = 0;
  c.height = 0;
}
